import { useMemo } from 'react';
import QRCode from 'qrcode';
import type { AssetCode } from '@onyx/shared';
import { AssetChip } from './AssetChip';

/** QR drawn as a single path in the ink of the band, the asset chip set into its center. */
export function QR({ value, asset, size = 220 }: { value: string; asset: AssetCode; size?: number }) {
  const { path, n } = useMemo(() => {
    const qr = QRCode.create(value, { errorCorrectionLevel: 'Q' });
    const count = qr.modules.size;
    const data = qr.modules.data;
    const hole = Math.ceil(count * 0.22);
    const lo = Math.floor((count - hole) / 2);
    const hi = lo + hole;
    const finder = (x: number, y: number) => (x < 7 && y < 7) || (x >= count - 7 && y < 7) || (x < 7 && y >= count - 7);
    let d = '';
    for (let y = 0; y < count; y++) {
      for (let x = 0; x < count; x++) {
        if (!data[y * count + x] || finder(x, y)) continue;
        if (x >= lo && x < hi && y >= lo && y < hi) continue;
        d += `M${x + 0.08},${y + 0.08}h0.84v0.84h-0.84z`;
      }
    }
    // Finder patterns as rounded squares: unmistakable for scanners, softer for the eye.
    for (const [fx, fy] of [[0, 0], [count - 7, 0], [0, count - 7]] as const) {
      d += `M${fx + 1.6},${fy}h3.8a1.6,1.6 0 0 1 1.6,1.6v3.8a1.6,1.6 0 0 1 -1.6,1.6h-3.8a1.6,1.6 0 0 1 -1.6,-1.6v-3.8a1.6,1.6 0 0 1 1.6,-1.6z`;
      d += `M${fx + 1.9},${fy + 1}h3.2a0.9,0.9 0 0 1 0.9,0.9v3.2a0.9,0.9 0 0 1 -0.9,0.9h-3.2a0.9,0.9 0 0 1 -0.9,-0.9v-3.2a0.9,0.9 0 0 1 0.9,-0.9z`;
      d += `M${fx + 2.9},${fy + 2}h1.2a0.9,0.9 0 0 1 0.9,0.9v1.2a0.9,0.9 0 0 1 -0.9,0.9h-1.2a0.9,0.9 0 0 1 -0.9,-0.9v-1.2a0.9,0.9 0 0 1 0.9,-0.9z`;
    }
    return { path: d, n: count };
  }, [value]);

  return (
    <div className="qr" style={{ width: size, height: size }}>
      <svg viewBox={`-2 -2 ${n + 4} ${n + 4}`} width={size} height={size} role="img" aria-label="QR">
        <rect x={-2} y={-2} width={n + 4} height={n + 4} rx={3} className="qr__bg" />
        <path d={path} className="qr__ink" fillRule="evenodd" />
      </svg>
      <span className="qr__center">
        <AssetChip asset={asset} size={Math.round(size * 0.18)} />
      </span>
    </div>
  );
}
